import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  MapPin,
  RefreshCw,
  Route,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { fetchSupabaseFunction } from "@/lib/shopifySessionToken";
import { openShopifyTopLevel } from "@/lib/shopifyEmbedded";
import { formatCurrency, type Currency } from "@/lib/currency";

interface ShippingCondition {
  conditionCriteria: {
    __typename: string;
    amount?: string;
    currencyCode?: string;
    unit?: string;
    value?: number;
  } | null;
  field: string;
  operator: string;
}

interface ShippingMethod {
  active: boolean;
  description: string | null;
  id: string;
  methodConditions: ShippingCondition[];
  name: string;
  rateProvider: {
    __typename: string;
    fixedFee?: { amount: string; currencyCode: string } | null;
    percentageOfRateFee?: number;
    price?: { amount: string; currencyCode: string };
  };
}

interface ShippingProfile {
  activeMethodDefinitionsCount: number;
  default: boolean;
  id: string;
  name: string;
  profileLocationGroups: Array<{
    locationGroup: {
      id: string;
      locations: { nodes: Array<{ id: string; name: string }> };
    };
    locationGroupZones: {
      nodes: Array<{
        methodDefinitions: { nodes: ShippingMethod[] };
        zone: {
          countries: Array<{
            code: { countryCode: string; restOfWorld: boolean };
            provinces: Array<{ code: string; name: string }>;
          }>;
          id: string;
          name: string;
        };
      }>;
    };
  }>;
}

interface ShippingCheck {
  id: string;
  product_title: string | null;
  shipping_address: Record<string, unknown> | null;
  shipping_resolution: {
    api_error?: string | null;
    evaluated_at?: string;
    fulfillment_mode?: "delivery" | "pickup";
    rates_returned?: Array<{ code: string; id: string; price: string; source: string; title: string }>;
    requested_address?: {
      city?: string;
      country?: string;
      province?: string;
      province_code?: string;
      zip?: string;
    };
    selected_rate?: { code: string; id: string; price: string; source: string; title: string } | null;
    source?: string;
    used_fallback?: boolean;
    warning?: string | null;
  };
  total_price: number;
  total_shipping: number;
  updated_at: string;
}

interface ShippingDetailsPayload {
  error?: string;
  hasShippingScope?: boolean;
  profiles?: ShippingProfile[];
  recentChecks?: ShippingCheck[];
  reconnectRequired?: boolean;
  scopes?: string[];
  shop?: { currencyCode: string; name: string };
  storeDomain?: string;
  success?: boolean;
}

interface ShopifyShippingDetailsProps {
  merchantId: string;
  storeDomain: string;
}

function formatCondition(condition: ShippingCondition): string {
  const criteria = condition.conditionCriteria;
  const value = criteria?.amount !== undefined
    ? `${criteria.currencyCode || ""} ${criteria.amount}`.trim()
    : criteria?.value !== undefined
      ? `${criteria.value} ${criteria.unit || ""}`.trim()
      : "configured value";
  return `${condition.field.replaceAll("_", " ")} ${condition.operator.replaceAll("_", " ")} ${value}`.toLowerCase();
}

function getMethodPrice(method: ShippingMethod): string {
  if (method.rateProvider.price) {
    return formatCurrency(Number(method.rateProvider.price.amount), method.rateProvider.price.currencyCode as Currency);
  }
  if (method.rateProvider.fixedFee) {
    return `${formatCurrency(Number(method.rateProvider.fixedFee.amount), method.rateProvider.fixedFee.currencyCode as Currency)} carrier fee`;
  }
  return "Carrier calculated";
}

export function ShopifyShippingDetails({ merchantId, storeDomain }: ShopifyShippingDetailsProps) {
  const [data, setData] = useState<ShippingDetailsPayload | null>(null);
  const [loading, setLoading] = useState(true);

  const loadDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchSupabaseFunction("get-shopify-shipping-details", {
        body: { merchantId },
      });
      const payload = await response.json().catch(() => ({})) as ShippingDetailsPayload;
      setData(payload);
      if (!response.ok && !payload.reconnectRequired) {
        throw new Error(payload.error || "Unable to load shipping details");
      }
    } catch (error) {
      console.error("Failed to load Shopify shipping details:", error);
      toast.error(error instanceof Error ? error.message : "Unable to load shipping details");
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    void loadDetails();
  }, [loadDetails]);

  const reconnectShopify = () => {
    const oauthUrl = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-oauth-init`);
    oauthUrl.searchParams.set("shop", storeDomain);
    oauthUrl.searchParams.set("merchant_id", merchantId);
    oauthUrl.searchParams.set("source", "dashboard");
    oauthUrl.searchParams.set("return_to", "dashboard");
    oauthUrl.searchParams.set("signup_source", "direct_website");
    openShopifyTopLevel(oauthUrl.toString());
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex min-h-64 items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Reading live Shopify shipping settings...
        </CardContent>
      </Card>
    );
  }

  const profiles = data?.profiles || [];
  const recentChecks = data?.recentChecks || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Shipping Details</h2>
          <p className="text-sm text-muted-foreground">
            Live Shopify zones and the shipping rates Splitkart selected during address validation.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void loadDetails()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => openShopifyTopLevel(`https://${storeDomain}/admin/settings/shipping`)}
          >
            Shopify settings <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {data?.reconnectRequired && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Shipping permission needs approval</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>{data.error}</p>
            <Button size="sm" onClick={reconnectShopify}>Reconnect Shopify</Button>
          </AlertDescription>
        </Alert>
      )}

      {data?.hasShippingScope && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Connected to Shopify shipping</AlertTitle>
          <AlertDescription>
            Showing live settings for {data.shop?.name || storeDomain}. Splitkart does not edit these rates.
          </AlertDescription>
        </Alert>
      )}

      {profiles.map((profile) => (
        <Card key={profile.id}>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>{profile.name}</CardTitle>
              {profile.default && <Badge variant="secondary">Default profile</Badge>}
              <Badge variant="outline">{profile.activeMethodDefinitionsCount} active rates</Badge>
            </div>
            <CardDescription>Configured in Shopify and read live by Splitkart.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {profile.profileLocationGroups.flatMap((group) => (
              group.locationGroupZones.nodes.map(({ zone, methodDefinitions }) => {
                const provinces = zone.countries.flatMap((country) => country.provinces);
                const countries = zone.countries.map((country) => country.code.restOfWorld ? "Rest of world" : country.code.countryCode);
                return (
                  <div key={`${group.locationGroup.id}-${zone.id}`} className="rounded-xl border p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 font-semibold">
                          <MapPin className="h-4 w-4 text-primary" /> {zone.name}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Locations: {group.locationGroup.locations.nodes.map((location) => location.name).join(", ") || "Shopify location group"}
                        </p>
                      </div>
                      <Badge variant="outline">{countries.join(", ")}</Badge>
                    </div>
                    {provinces.length > 0 && (
                      <p className="mt-3 text-sm">
                        <span className="font-medium">States:</span>{" "}
                        {provinces.map((province) => `${province.name} (${province.code})`).join(", ")}
                      </p>
                    )}
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      {methodDefinitions.nodes.map((method) => (
                        <div key={method.id} className="rounded-lg bg-muted/50 p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{method.name}</span>
                            <Badge variant={method.active ? "default" : "secondary"}>{method.active ? "Active" : "Inactive"}</Badge>
                          </div>
                          <p className="mt-1 text-sm font-semibold text-primary">{getMethodPrice(method)}</p>
                          {method.description && <p className="mt-1 text-xs text-muted-foreground">{method.description}</p>}
                          {method.methodConditions.length > 0 && (
                            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                              {method.methodConditions.map((condition, index) => (
                                <p key={`${method.id}-${index}`}>Applies when {formatCondition(condition)}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            ))}
          </CardContent>
        </Card>
      ))}

      {!data?.reconnectRequired && profiles.length === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Shopify delivery profiles returned</AlertTitle>
          <AlertDescription>Open Shopify shipping settings and confirm the products and locations have active rates.</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Route className="h-5 w-5" /> Recent Splitkart shipping checks</CardTitle>
          <CardDescription>Use these records to verify the state/PIN submitted and the exact Shopify rate selected.</CardDescription>
        </CardHeader>
        <CardContent>
          {recentChecks.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No diagnostic records yet. New address validations will appear here.
            </p>
          ) : (
            <div className="space-y-3">
              {recentChecks.map((check) => {
                const resolution = check.shipping_resolution || {};
                const selected = resolution.selected_rate;
                const requested = resolution.requested_address || {};
                return (
                  <details key={check.id} className="rounded-xl border p-4">
                    <summary className="cursor-pointer list-none">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="font-medium">{check.product_title || "Split order"}</p>
                          <p className="text-sm text-muted-foreground">
                            {[requested.city, requested.province, requested.province_code, requested.zip].filter(Boolean).join(", ")}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={resolution.used_fallback ? "destructive" : "default"}>
                            {resolution.used_fallback ? "Fallback" : selected ? "Shopify rate" : "No rate"}
                          </Badge>
                          <span className="text-sm font-semibold">
                            {formatCurrency(Number(check.total_shipping || 0), (data?.shop?.currencyCode || "INR") as Currency)}
                          </span>
                        </div>
                      </div>
                    </summary>
                    <div className="mt-4 grid gap-4 border-t pt-4 md:grid-cols-2">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Selected by Splitkart</p>
                        <p className="mt-1 text-sm font-medium">{selected?.title || resolution.source || "No Shopify rate selected"}</p>
                        <p className="text-xs text-muted-foreground">
                          Requested: {resolution.fulfillment_mode === "pickup" ? "Store pickup" : "Delivery"}
                        </p>
                        {selected && <p className="text-xs text-muted-foreground">Code: {selected.code || "-"} · Source: {selected.source || "Shopify"}</p>}
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">All rates Shopify returned</p>
                        {(resolution.rates_returned || []).length > 0 ? (
                          <div className="mt-1 space-y-1 text-sm">
                            {(resolution.rates_returned || []).map((rate) => (
                              <p key={rate.id}>{rate.title}: {formatCurrency(Number(rate.price), (data?.shop?.currencyCode || "INR") as Currency)}</p>
                            ))}
                          </div>
                        ) : <p className="mt-1 text-sm text-muted-foreground">None</p>}
                      </div>
                    </div>
                    {(resolution.warning || resolution.api_error) && (
                      <Alert variant={resolution.api_error ? "destructive" : "default"} className="mt-4">
                        <Truck className="h-4 w-4" />
                        <AlertDescription>{resolution.api_error || resolution.warning}</AlertDescription>
                      </Alert>
                    )}
                    <p className="mt-3 text-xs text-muted-foreground">Checked {new Date(check.updated_at).toLocaleString()}</p>
                  </details>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
